import React, { useState, useEffect, useCallback } from 'react';
import './AppCard.css';

function getStartTime(range) {
  const now = new Date();
  const ms = { day: 86400000, week: 604800000, month: 2592000000 };
  return new Date(now - (ms[range] || ms.week)).toISOString();
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function AppCard({ app, timeRange, apiCall, onDataLoaded, onViewDetails }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const startTime = getStartTime(timeRange);
      const { subscriptionId, resourceGroup, workflowName } = app;
      const result = await apiCall(
        `runs?subscriptionId=${subscriptionId}&resourceGroup=${encodeURIComponent(resourceGroup)}&workflowName=${encodeURIComponent(workflowName)}&startTime=${encodeURIComponent(startTime)}`
      );
      setData(result);
      onDataLoaded?.(app.id, { failed: result.failed, succeeded: result.succeeded, total: result.total });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [app, timeRange, apiCall, onDataLoaded]);

  useEffect(() => { loadData(); }, [loadData]);

  const completed   = data ? data.succeeded + data.failed : 0;
  const successRate = completed > 0
    ? Math.round((data.succeeded / completed) * 100)
    : null;

  const healthClass = successRate === null ? '' :
    successRate >= 95 ? 'healthy' :
    successRate >= 80 ? 'warning' : 'critical';

  return (
    <div className={`app-card ${loading ? 'loading' : ''} ${error ? 'errored' : ''}`}>

      {/* Header — fixed structure, always the same rows */}
      <div className="app-card-header">
        <div className="app-card-title-row">
          <h3 className="app-card-title">{app.displayName}</h3>
          {!loading && !error && data && (
            <span className={`health-dot ${healthClass}`} title={`${successRate ?? '—'}% success rate`} />
          )}
        </div>
        <p className="app-card-workflow">{app.workflowName}</p>
        <div className="app-card-status" aria-live="polite">
          {data && !loading && !error && (
            <>
              {data.running > 0 && (
                <span className="live-pill running"><span className="live-dot" />{data.running} running</span>
              )}
              {data.waiting > 0 && (
                <span className="live-pill waiting"><span className="waiting-dot" />{data.waiting} queued</span>
              )}
              {!data.running && !data.waiting && (
                <span className="last-run">Last run: {formatDate(data.lastRun)}</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Body — fills remaining space */}
      <div className="app-card-body">
        {loading && (
          <div className="skeleton-rows">
            <div className="skeleton-row" />
            <div className="skeleton-row short" />
          </div>
        )}

        {error && !loading && (
          <div className="app-card-error">
            <span className="error-icon">&#9888;</span>
            <span>{error}</span>
            <button className="retry-btn" onClick={loadData}>Retry</button>
          </div>
        )}

        {data && !loading && !error && (
          <>
            <div className="stats-row">
              <div className="stat">
                <span className="stat-value">{data.total}</span>
                <span className="stat-label">Total</span>
              </div>
              <div className="stat">
                <span className={`stat-value ${data.failed > 0 ? 'failed' : 'ok'}`}>{data.failed}</span>
                <span className="stat-label">Failed</span>
              </div>
              <div className="stat">
                <span className="stat-value succeeded">{data.succeeded}</span>
                <span className="stat-label">Succeeded</span>
              </div>
            </div>

            {successRate !== null && (
              <div className="success-bar-wrapper">
                <div className="success-bar-track">
                  <div
                    className={`success-bar-fill ${healthClass}`}
                    style={{ width: `${successRate}%` }}
                  />
                </div>
                <span className="success-bar-label">{successRate}% success</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer — always at bottom, outside the body */}
      <div className="app-card-footer">
        <a
          className="azure-portal-btn"
          href={`https://portal.azure.com/#resource/subscriptions/${app.subscriptionId}/resourceGroups/${app.resourceGroup}/providers/Microsoft.Logic/workflows/${app.workflowName}/runs`}
          target="_blank"
          rel="noreferrer"
        >
          Open in Azure
        </a>
        {data?.failed > 0 && (
          <button
            className="view-errors-btn"
            onClick={() => onViewDetails(app, data.failedRuns)}
          >
            View errors ({data.failed})
          </button>
        )}
      </div>

    </div>
  );
}

export default AppCard;
